import React, { useContext, useEffect } from 'react';
import propTypes from 'prop-types';
import { extend, QueryContext } from '@thd-nucleus/data-sources';
import { ExperienceContext, useConfigService, useStore } from '@thd-nucleus/experience-context';
import { CustomerContext } from '@thd-olt-functional/customer-information';
import { LayoutRenderer } from '@thd-olt-component-react/layout-renderer';
import { declareContexts } from '@thd-olt-component-react/hydrator';
import { useLocation } from '@thd-olt-component-react/router';
import { useUrlTmxId } from '../../hooks/useUrlTmxId';
import { componentOptions, componentOptionsLayoutA, componentOptionsLayoutB } from './component-options';
import {
  CUSTOMER_TYPES,
  createContentInformationApiErrorHandler,
  getGuestUserId,
  getHydrationOptions,
  getMarketingCloudVisitorID,
  getProCustomerSegment,
  initializeThdOnlineChannelCookie,
  triggerHomepageLoadEvent,
  userAuthorizationStatus
} from '../../utils';
import {
  homepagePreviewByIDDataModelClientSide,
  homepagePreviewByIDDataModelServerSide,
  proDataModelClientSide,
  proDataModelServerSide
} from '../../datamodels/home';
import { components } from '../../components/components';
import { ProHomePageLoadingLayout } from './ProHomePageLoadingLayout';
import { usePreviewByID } from '../../hooks';
import { HomepageContext, HomepageProvider } from '../../components/context';
import { HomepageMetadataContainer } from '../../components/metadata/HomepageMetadataContainer';

const { string, bool } = propTypes;

let layoutDataModel = {
  server: extend({}, proDataModelServerSide),
  client: proDataModelClientSide
};

export const ProHomepage = ({ slug, thdSEOBotDetection }) => {
  declareContexts([QueryContext, ExperienceContext, HomepageContext, CustomerContext], { freeze: true });
  const { channel, isConsumerApp } = useContext(ExperienceContext);
  const store = useStore({ varnish: false });
  const userState = {
    mcvisID: getMarketingCloudVisitorID(),
    userAuthStatus: userAuthorizationStatus(),
    guestUserId: getGuestUserId(),
    proCustomerSegment: getProCustomerSegment(),
  };

  const contentfulPreviewId = usePreviewByID();
  const proHPBackup = useConfigService('proHPBackup');
  const { search: querystring, pathname } = useLocation();

  const pathWithQueryParams = `${pathname}${querystring}`;
  const isPreviewPage = pathWithQueryParams.indexOf('render*') > -1
    || pathWithQueryParams.indexOf('contentPV') > -1
    || pathWithQueryParams.indexOf('bassPV') > -1;

  /**
   * This is not a class in the CSS sense. It's referring
   * to the type of layout we have in Contentful, which
   * at the time of writing seems to be a choice between
   * PersonalizedHomepage and EducationalLayout.
   */
  let layoutClass;

  // Workspace Dashboard requires different widths for some components based on layout choice.
  // eslint-disable-next-line no-nested-ternary
  const slugBasedComponentOptions = (slug === 'homepage-prod-wd-a-b2b') ? componentOptionsLayoutA
    : (slug === 'homepage-prod-wd-b-b2b' ? componentOptionsLayoutB : componentOptions);

  if (contentfulPreviewId) {
    layoutDataModel = {
      server: homepagePreviewByIDDataModelServerSide,
      client: homepagePreviewByIDDataModelClientSide
    };
    layoutClass = 'PersonalizedHomePage';
  }

  useEffect(() => {
    triggerHomepageLoadEvent({ eventName: 'pro-homepage' });
    initializeThdOnlineChannelCookie(channel, isConsumerApp);
  }, []);

  const { guestUserId, mcvisID, userAuthStatus, proCustomerSegment } = userState;

  return (
    <HomepageProvider
      value={{
        thdSEOBotDetection,
        hydrationOptions: getHydrationOptions(channel, isConsumerApp),
        customerType: CUSTOMER_TYPES.B2B,
        urlTmxId: useUrlTmxId(),
        guestUserId,
        mcvisID,
        store,
        userAuthStatus,
        proCustomerSegment,
      }}
    >
      <LayoutRenderer
        components={components}
        componentOptions={slugBasedComponentOptions}
        slug={slug}
        contentfulPreviewId={contentfulPreviewId}
        layoutClass={layoutClass}
        layoutDataModel={layoutDataModel}
        loadingLayout={<ProHomePageLoadingLayout />}
        onContentInformationApiError={createContentInformationApiErrorHandler({
          component: 'ProHomepage',
          redirectTarget: proHPBackup
        })}
        ssr
        renderMetadata={({ preloadedImages, metadata }) => {
          return (
            <HomepageMetadataContainer
              isPreviewPage={isPreviewPage}
              preloadedImages={preloadedImages}
              metadata={metadata}
            />
          );
        }}
        customerType={CUSTOMER_TYPES.B2B}
      />
    </HomepageProvider>
  );
};

ProHomepage.dataModel = layoutDataModel;

ProHomepage.propTypes = {
  slug: string,
  thdSEOBotDetection: bool
};

ProHomepage.defaultProps = {
  slug: undefined,
  thdSEOBotDetection: false
};

ProHomepage.displayName = 'ProHomepage';
